import BlockyBoldText from "components/custom-texts/blocky-bold-text";
import { Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useAppSelector } from "state/hooks";
import {
  getContestantColor,
  getGroupState,
  getTextColor,
} from "state/slices/group-slice";
import { isPoolClosed } from "utils/pool/is-pool-closed";
import { Entry } from "interfaces/entry.ts";
import useContestContestantPickAllowedCheck from "hooks/useContestEntryCheck.ts";
import BlockyHeavyText from "components/custom-texts/blocky-heavy-text";
import { CheckCircle } from "react-bootstrap-icons";
import { POOL_STATUS } from "utils/constants.ts";
import { formatWinPercent } from "utils/formatter-utils/win-percent-formatter";
import { winOddsCellStyle } from "components/homepage-pools-list/win-odds-column-styles";

interface Props {
  entry: Entry;
  wasClicked: boolean;
  handleContestantClick: (entry: any) => void;
  totalSeedDistribution: number;
  poolStatus: number;
  contestEntryId?: string;
  winnerPoolEntryIds?: string[];
  alreadyPicked?: boolean;
}

const ContestantCard = ({
  entry,
  handleContestantClick,
  wasClicked,
  totalSeedDistribution,
  poolStatus,
  contestEntryId,
  winnerPoolEntryIds = [],
  alreadyPicked = false,
}: Props) => {
  const textColor = useAppSelector(getTextColor);
  const closedPool = isPoolClosed(poolStatus);
  const group = useAppSelector(getGroupState);
  const contestantColor = useAppSelector(getContestantColor);

  const { isPickAllowed: hookAllowed } = useContestContestantPickAllowedCheck(
    entry.contestant_name,
    contestEntryId
  );
  const isPickAllowed = hookAllowed && !alreadyPicked;

  const projectedWin = entry.seed_distribution / totalSeedDistribution || 0;
  
  const isWinner = winnerPoolEntryIds.includes(entry.pool_entry_id);
  const isSettledPool = poolStatus === POOL_STATUS.SETTLED;

  const openPoolStyles = {
    border: wasClicked ? "1px solid black" : "none",
    backgroundColor: wasClicked
      ? group.settings.secondary_color
      : contestantColor,
    color: wasClicked ? "black" : textColor,
    opacity: isPickAllowed ? 1 : 0.5,
    cursor: isPickAllowed ? "pointer" : "not-allowed",
  };

  const closedPoolStyles = {
    backgroundColor: "gray",
    opacity: 1,
    cursor: "default",
  };

  const winnerStyles = {
    border: "3px solid var(--winner-green)",
    backgroundColor: "lightgray",
    opacity: 1,
    cursor: "default",
  };

  const conditionalStyles = isSettledPool && isWinner
    ? winnerStyles
    : isPoolClosed(poolStatus)
    ? closedPoolStyles
    : openPoolStyles;

  const cardComponent = (
    <Card
      key={entry.pool_entry_id}
      onClick={(e) => {
        e.preventDefault();
        if (isPickAllowed && !closedPool) {
          handleContestantClick(entry);
        }
      }}
      style={{
        padding: 3,
        paddingLeft: 5,
        paddingRight: 5,
        marginBottom: 5,
        ...conditionalStyles,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div style={{ maxWidth: "65%", display: "flex", alignItems: "center" }}>
          {isSettledPool && isWinner && (
            <CheckCircle
              size={12}
              style={{ color: "green", marginRight: 4 }}
            />
          )}
          <BlockyHeavyText>{entry.contestant_name}</BlockyHeavyText>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            fontSize: 12,
            width: 84,
          }}
        >
          <BlockyBoldText style={winOddsCellStyle}>
            {formatWinPercent(projectedWin)}
          </BlockyBoldText>
          <BlockyBoldText style={winOddsCellStyle}>
            {entry.odds.toFixed(1)}X
          </BlockyBoldText>
        </div>
      </div>
    </Card>
  );

  return !isPickAllowed ? (
    <OverlayTrigger
      placement="top"
      delay={{ show: 250, hide: 400 }}
      overlay={(props: any) => (
        <Tooltip id={`tooltip-${entry.pool_entry_id}`} {...props}>
          <div style={{ color: "red", fontSize: "0.8rem" }}>
            Already picked in this contest
          </div>
        </Tooltip>
      )}
    >
      <div>{cardComponent}</div>
    </OverlayTrigger>
  ) : (
    cardComponent
  );
};

export default ContestantCard;
